/**
 * Textos por perfil dos itens que pedem ação (GUPM-206, fiel ao `buildNotifications` do V8).
 * TS puro.
 *
 * Viviam no `AnomalyList`. Saíram para o domínio no C-24 para o sino do header e o
 * "Precisa da sua atenção" dizerem a MESMA frase sobre o mesmo instrumento:
 *  - Vigilante (operador): linguagem simples ("o que fazer agora").
 *  - RT/Admin: linguagem técnica (valor, unidade, seção).
 */
import { rotuloDaEstrutura } from '../estrutura/rotulo';
import { tempoSemSinal } from '../instrumentos/perda-de-sinal';
import { nomeDoTipoDeInstrumento } from '../tarp/catalog';
import type { TarpLevel } from '../types';
import type { ItemAtencao } from './atencao';

/**
 * Rótulo do nível nos textos. Espelha `LEVEL_UI[nivel].label` (`src/ui/tokens.ts`), que o
 * domínio não pode importar — um teste trava a igualdade das duas tabelas.
 */
export const ROTULO_NIVEL: Record<TarpLevel, string> = {
  NORMAL: 'Normal',
  ATENCAO: 'Atenção',
  ALERTA: 'Alerta',
  EMERGENCIA: 'Emergência',
};

/**
 * Nome do botão do item, igual no Painel e no sino — no V8 os dois usam o mesmo
 * `action.label`: perda de sinal abre o GUIA (TARP-PS-*), nível abre o fluxo do instrumento.
 */
export function rotuloDaAcaoDeAtencao(item: ItemAtencao): 'Abrir guia' | 'Abrir fluxo TARP' {
  return item.kind === 'sem-sinal' ? 'Abrir guia' : 'Abrir fluxo TARP';
}

/** Título por perfil: vigilante recebe rótulo genérico; RT vê instrumento + nível. */
export function tituloDoItemDeAtencao(item: ItemAtencao, isVigilante: boolean, agora: Date): string {
  if (item.kind === 'sem-sinal') {
    const tempo = tempoSemSinal(item.silencioDesde, agora);
    // O operador precisa saber O QUE é (tiltímetro = sensor de ruptura) e por quanto
    // tempo; o código do sensor vem na descrição, junto da barragem. C-88: o título nomeia
    // o tipo pelo cadastro (hoje só o tiltímetro chega aqui; generalizar é o GUPM-308).
    const tipo = nomeDoTipoDeInstrumento(item.instrument.type) ?? 'Instrumento';
    return isVigilante
      ? `${tipo} sem sinal há ${tempo}`
      : `${item.instrument.code} sem sinal há ${tempo}`;
  }
  if (!isVigilante) return `${item.instrument.label} em ${ROTULO_NIVEL[item.level]}`;
  if (item.level === 'EMERGENCIA') return 'Sensor em emergência';
  if (item.level === 'ALERTA') return 'Sensor com valor crítico';
  return 'Sensor acima do normal'; // ATENCAO
}

/**
 * `HH:mm · dd/MM` do início do silêncio, em horário LOCAL — mesma convenção do
 * `CronTimer` (`toLocaleTimeString('pt-BR')`). O operador lê o relógio da parede da
 * barragem; imprimir UTC faria ele calcular o fuso de cabeça durante um plantão.
 * Sem segundos: a resolução do dado é a cadência de publicação, 30 min.
 */
function quandoSilencio(d: Date): string {
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dia = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  return `${hora} · ${dia}`;
}

/** Descrição por perfil: vigilante recebe instrução; RT vê a leitura técnica. */
export function descricaoDoItemDeAtencao(item: ItemAtencao, isVigilante: boolean): string {
  // A barragem é obrigatória nos DOIS perfis e nos DOIS tipos de item: `TIL-01` existe em
  // A, B e C, e 18 códigos se repetem entre as estruturas (C-24: o sino mostra o item fora
  // do Painel, sem o contexto da tabela por barragem). Rótulo pelo helper (GUPM-329): a
  // Mina não é "Barragem MINA".
  const estrutura = rotuloDaEstrutura(item.damCode) ?? 'Estrutura não identificada';
  if (item.kind === 'sem-sinal') {
    return isVigilante
      ? `Sensor ${item.instrument.code} · ${estrutura} — siga o protocolo TARP.`
      : `${item.instrument.code} · ${estrutura} · sem sinal desde ${quandoSilencio(item.silencioDesde)}`;
  }
  const { instrument: i, value, unit } = item;
  if (isVigilante) return `Sensor ${i.code} · ${estrutura} — siga o protocolo TARP.`;
  const leitura = unit ? `${value ?? '—'} ${unit}` : `${value ?? '—'}`;
  return `${i.label} · ${estrutura} · ${leitura} · Seção ${i.section ?? '—'}`;
}
